/**
 * Shared helpers for brand-connect cache history (feature / request drafts).
 * Keeps PitchPortal label logic aligned with BrandConnect pre-fill behaviour.
 */

function cloneSnapshot(snapshot) {
	try {
		return JSON.parse(JSON.stringify(snapshot));
	} catch (e) {
		return snapshot;
	}
}

function normaliseSnapshotForComparison(snapshot) {
	const s = snapshot && typeof snapshot === "object" ? snapshot : null;
	if (!s) return null;

	const copy = cloneSnapshot(s);
	if (!copy || typeof copy !== "object") return copy;

	if (typeof copy.savedAt !== "undefined") {
		delete copy.savedAt;
	}

	if (copy.briefWriter && typeof copy.briefWriter === "object") {
		if (typeof copy.briefWriter.updatedAt !== "undefined") {
			delete copy.briefWriter.updatedAt;
		}
	}

	return copy;
}

export function isSnapshotEquivalent(a, b) {
	if (a === b) return true;
	if (!a || !b) return false;

	const aa = normaliseSnapshotForComparison(a);
	const bb = normaliseSnapshotForComparison(b);

	try {
		return JSON.stringify(aa) === JSON.stringify(bb);
	} catch (e) {
		return false;
	}
}

/**
 * API returns entries newest-first (ORDER BY id DESC). Reverse to oldest-first,
 * then drop consecutive duplicates — same as BrandConnect.loadBrandConnectHistory.
 *
 * @param {Array<{ id?: *, createdAt?: string, created_at?: string, snapshot?: * }>} entries
 * @returns {Array<{ id: *, createdAt: string|null, snapshot: * }>}
 */
export function buildDedupedBrandConnectHistoryEntries(entries) {
	if (!Array.isArray(entries) || !entries.length) return [];

	const ordered = entries.slice().reverse();
	const deduped = [];

	ordered.forEach((entry) => {
		const nextEntry = {
			id: entry.id,
			createdAt: entry.createdAt || entry.created_at || null,
			snapshot: entry.snapshot || null,
		};
		const last = deduped[deduped.length - 1];
		if (!last || !isSnapshotEquivalent(last.snapshot, nextEntry.snapshot)) {
			deduped.push(nextEntry);
		}
	});

	return deduped;
}

/**
 * True if opening the feature BrandConnect modal would apply non-empty draft fields
 * (matches BrandConnect cache / history semantics).
 */
export function hasMeaningfulBrandConnectFeatureSnapshot(snapshot) {
	const s = snapshot && typeof snapshot === "object" ? snapshot : {};
	const d = s.data && typeof s.data === "object" ? s.data : {};
	const br = d.brandRequirements && typeof d.brandRequirements === "object" ? d.brandRequirements : {};
	const cc = d.customContact && typeof d.customContact === "object" ? d.customContact : {};
	const bw = s.briefWriter && typeof s.briefWriter === "object" ? s.briefWriter : {};

	const msg = typeof d.message === "string" ? d.message.trim() : "";
	const t = typeof d.title === "string" ? d.title.trim() : "";
	const partnerships = Array.isArray(d.partnerships) ? d.partnerships : [];
	const wants = Array.isArray(br.wants) ? br.wants : [];
	const bads = Array.isArray(br.bads) ? br.bads : [];
	const wantInput = typeof br.want === "string" ? br.want.trim() : "";
	const badInput = typeof br.bad === "string" ? br.bad.trim() : "";
	const lifestyle = typeof d.lifestyleImage === "string" ? d.lifestyleImage.trim() : "";
	const email = typeof cc.email === "string" ? cc.email.trim() : "";

	const bwOpen = bw.open === true;
	const bwRunIdRaw = bw.runId != null ? parseInt(bw.runId, 10) : null;
	const bwRunId = Number.isFinite(bwRunIdRaw) ? bwRunIdRaw : null;
	const bwRefineNotes = typeof bw.refineNotesDraft === "string" ? bw.refineNotesDraft.trim() : "";

	return !!(
		msg ||
		t ||
		partnerships.length ||
		wants.length ||
		bads.length ||
		wantInput ||
		badInput ||
		lifestyle ||
		email ||
		bwOpen ||
		!!bwRunId ||
		!!bwRefineNotes
	);
}

/**
 * @param {{ entries?: Array }} data - JSON body from GET brand-connect/cache/history
 * @returns {boolean}
 */
export function featureCacheHistoryIndicatesPrefill(data) {
	const entries = Array.isArray(data?.entries) ? data.entries : [];
	const deduped = buildDedupedBrandConnectHistoryEntries(entries);
	if (!deduped.length) return false;
	const latest = deduped[deduped.length - 1];
	return !!(latest?.snapshot && hasMeaningfulBrandConnectFeatureSnapshot(latest.snapshot));
}
